import { CommonModule } from '@angular/common';
import {
  Component,
  Inject,
  ViewChild,
  ViewEncapsulation,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  MotifModalModule,
  MotifButtonModule,
  MotifFormsModule,
  MotifIconModule,
  MotifTabNavigationModule,
  MotifTableModule,
  ButtonSizes,
  ModalSizes,
  MotifModal,
  MotifPaginationModule,
  MOTIF_MODAL_DATA,
  MotifTooltipModule,
  MotifModalRef,
} from '@ey-xd/ng-motif';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import {
  TranslatePipe,
  TranslateDirective,
  TranslateService,
} from '@ngx-translate/core';
import { UserSearchComponent } from '@shared/user-search/user-search.component';
import {
  IconoirXmark,
  IconoirNavArrowDown,
  IconoirGroup,
  IconoirCommunity,
} from '@ey-xd/motif-icon';
import { AccessType } from '@models/userManagement';
import { GridApi, GridOptions } from 'ag-grid-community';
import { SecureStorageService } from '@services/securestorage.service';
import { AccessTypeComponent } from './access-type/access-type.component';
import { UserActionComponent } from './user-action/user-action.component';
import { UserService } from '@services/user.service';
import { UserDto } from '@models/userDto';
import { GridUserCard } from '@shared/template/grid_user_card';
import { UserManagementService } from '@services/user-management.service';
import { forkJoin } from 'rxjs';
import { CommonService } from '@services/common.service';
import { ResponseDto } from '@models/responseDto';
import { ConfirmDialogComponent } from '@shared/confirm-dialog/confirm-dialog.component';
import { ApplicationConstants } from '@constants/app.constants';

@Component({
  selector: 'app-user-management',
  imports: [
    CommonModule,
    TranslatePipe,
    TranslateDirective,
    MotifModalModule,
    MotifButtonModule,
    MotifIconModule,
    MotifFormsModule,
    MotifTabNavigationModule,
    MotifTableModule,
    ReactiveFormsModule,
    UserSearchComponent,
    MotifPaginationModule,
    MotifTooltipModule,
  ],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class UserManagementComponent {
  @Output() modalWidthChange = new EventEmitter<string>();
  buttonSizes = ButtonSizes;
  arrowDownIcon = IconoirNavArrowDown;
  groupIcon = IconoirGroup;
  closeIcon = IconoirXmark;
  iconCommunity = IconoirCommunity;
  currentOption = 1;

  setTab(tab: number) {
    this.currentOption = tab;
    this.emitModalWidth();
  }

  private emitModalWidth() {
    // 1 = Invite, 2 = SharedWith
    const width = this.currentOption === 2 ? '80vw' : '38vw';
    this.modalWidthChange.emit(width);
  }
  pagination = true;
  accessType: AccessType[] = [];
  projectList: any[] = [];
  userColDef: any[] = [];
  rowData: UserDto[] = [];
  gridApi: GridApi;
  paginationPageSize: 10;
  variant = null;
  currentlySelectedPageSize = { disable: false, value: 10, name: '10', id: 0 };
  paginationSelectList: any = [];
  selectedTheme: string;
  searchText = '';
  userInfo: any;
  isIPORModule: boolean = this.commonService.isIPORModule();
  userManagementForm = new FormGroup({
    accessType: new FormControl('', Validators.required),
    project: new FormControl<any>(null, Validators.required),
    projectName: new FormControl<any>(null), // Added projectName control
    userDetails: new FormControl<any>(null, [
      Validators.required,
      this.validateUserEmail,
    ]),
  });
  selectedRole: any;
  selectedProject: any;
  isExternalUser: boolean = false;
  gridOptions: GridOptions = {
    suppressHorizontalScroll: true, // Removes horizontal scroll
    suppressRowClickSelection: true,
    suppressRowHoverHighlight: true,
    defaultColDef: {
      resizable: false,
      suppressMovable: true,
    },
  };

  isFromProject: boolean = false;
  projectId: string = '';
  @ViewChild(UserSearchComponent) userSearchComponent: UserSearchComponent;
  constructor(
    private readonly translate: TranslateService,
    private readonly storageService: SecureStorageService,
    private readonly userService: UserService,
    private readonly userManagementService: UserManagementService,
    private readonly commonService: CommonService,
    public modal: MotifModal,
    @Inject(MOTIF_MODAL_DATA) data: any,
    private readonly dialogRef: MotifModalRef<UserManagementComponent>,
  ) {
    this.isFromProject = !!data;
    if (this.isFromProject) {
      this.projectId = data;
    }
  }

  ngDoCheck() {
    this.selectedTheme =
      this.storageService.getLocalStorageData('selectedtheme');
  }

  ngOnInit() {
    this.initializeComponent();
    this.emitModalWidth();
  }

  private initializeComponent(): void {
    this.userInfo = this.storageService.getSessionStorageData('userInfo');
    this.paginationSelectList = ApplicationConstants.paginationSelectList;
    forkJoin([
      this.userManagementService.getAllAccessTypes(),
      this.userService.getAllProjects(this.userInfo.Id),
    ]).subscribe({
      next: ([accessTypes, projects]) => {
        this.accessType = accessTypes.Data;
        if (projects.success && projects.Data.length > 0) {
          this.projectList = projects.Data.filter(
            (x) => x.Status === 'Approved',
          );
          if (this.isFromProject) {
            this.projectList = this.projectList.filter(
              (x) => x.ProjectId === this.projectId,
            );
            if (this.projectId && this.projectList?.length) {
              this.userManagementForm
                .get('project')
                ?.setValue(this.projectList[0]);
              this.userManagementForm
                .get('projectName')
                ?.setValue(this.projectList[0].ProjectName);
            }
          }
        }
      },
      error: (error) => {
        console.error('Error initializing component', error);
      },
    });
  }

  profileHandler(userListData = null) {
    this.currentOption = 2;
    this.clearForm();
    this.initiaiseCollDef();
    this.isExternalUser = false;
    if (!userListData) {
      this.userService
        .getAllAccessibleUsers(this.userInfo.Id)
        .subscribe((userData: any) => {
          this.initialiseRowData(userData.Data);
        });
    }
  }

  initialiseRowData(rowData: any[]): void {
    if (this.projectList.length > 0) {
      // Create a Set of ProjectIds for faster lookup
      const projectIdSet = new Set(
        this.projectList.map((project) => project.ProjectId),
      );
      // Filter the rowData based on the ProjectId Set
      this.rowData = rowData.filter((user) => projectIdSet.has(user.ProjectId));
    } else {
      this.rowData = rowData; // If no projects, assign the original rowData
    }
  }

  initiaiseCollDef() {
    this.userColDef = [
      {
        headerName: this.translate.instant('usersLabel'),
        field: 'DisplayName',
        cellRenderer: GridUserCard,
        width: 220,
        cellRendererParams: { context: this, isCustomDisplay: true },
        tooltipField: 'DisplayName',
      },
      {
        headerName: this.translate.instant('ProjectLabel'),
        field: 'ProjectName',
        sortable: true,
        width: 300,
        headerClass: 'header-center',
        cellRenderer: AccessTypeComponent,
        cellRendererParams: (params) => ({
          values: this.projectList.map((project) => ({
            name: project.ProjectName,
            id: project.ProjectId,
          })),
          isRole: false,
          selectedValue: {
            name: params.data.ProjectName,
            id: params.data.ProjectId,
          },
          selectedData: this.projectTypeHandler.bind(this, params.data),
        }),
      },
      {
        headerName: this.translate.instant('RoleLabel'),
        field: 'RoleName',
        sortable: true,
        width: 100,
        headerClass: 'header-center',
        cellRenderer: AccessTypeComponent,
        cellRendererParams: (params) => ({
          values: this.accessType.map((role) => ({
            name: role.RoleName,
            id: role.RoleId,
          })),
          isRole: true,
          disabled: params.data.Email
            ? this.commonService.isExternalUserByEmail(params.data.Email)
            : false,
          selectedValue: { name: params.data.RoleName, id: params.data.RoleId },
          selectedData: this.accessTypeHandler.bind(this, params.data),
        }),
      },
      {
        headerName: this.translate.instant('Actions'),
        sortable: false,
        width: 80,
        cellRenderer: UserActionComponent,
        cellClass: 'buttonAction',
        cellRendererParams: {
          deleteUserHandler: this.deleteUserHandler.bind(this),
          saveIconHandler: this.saveIconHandler.bind(this),
          selectedRole: this.selectedRole,
          selectedProject: this.selectedProject,
        },
      },
    ];
  }

  handleGridReady(params: any) {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  accessTypeHandler(params, selectedValue) {
    this.selectedRole = selectedValue;
  }

  projectTypeHandler(params, selectedValue) {
    this.selectedProject = selectedValue;
  }

  saveIconHandler(params) {
    if (this.selectedRole || this.selectedProject) {
      // this.commonService.startLoader();
      const userProjectId = params.data.UserProjectId;
      const userProjectDetails = {
        UserRoleId: userProjectId,
        RoleId: this.selectedRole ? this.selectedRole.id : params.data.RoleId,
        ProjectId: this.selectedProject
          ? this.selectedProject.id
          : params.data.ProjectId,
        UserId: params.data.Id,
        LoggedInUserId: this.userInfo.Id,
      };

      this.userService.updateUserRole(userProjectDetails).subscribe({
        next: (response: ResponseDto) => {
          if (response.success) {
            this.userService.getAllAccessibleUsers(this.userInfo.Id).subscribe({
              next: (userData: any) => {
                this.initialiseRowData(userData.Data);
                this.commonService.showTranslatedToasterMessage(
                  'AccessUpdateSuccessMesssage',
                  {
                    username: params.data.DisplayName,
                    name: this.selectedProject
                      ? this.selectedProject.name
                      : params.data.ProjectName,
                  },
                  'success',
                );
                // this.commonService.stopLoader();
              },
              error: (error) => {
                console.error('Error fetching accessible users', error);
                // this.commonService.stopLoader();
              },
            });
          }
        },
        error: (error) => {
          console.error('Error updating user role', error);
          // this.commonService.stopLoader();
        },
      });
    }
  }

  deleteUserHandler(params) {
    console.log(params);
    const message = this.commonService.getTranslationMessage(
      'RevokeUserAccessMessage',
      {
        username: params.data.DisplayName,
        projectname: params.data.ProjectName,
      },
    );
    const messageTitle = this.commonService.getTranslationMessage(
      'ConfirmUserRevokeAceessHeader',
      { username: params.data.DisplayName },
    );
    let modalRef = this.modal.open(ConfirmDialogComponent, {
      size: ModalSizes.XL,
      disableClose: true,
      data: {
        description: message,
        name: messageTitle,
        action: true,
        okButtonText: 'ConfirmText',
        cancelButtonText: 'Cancel',
      },
    });

    modalRef.afterClosed().subscribe((result) => {
      console.log(`Dialog result: ${result}`);
      console.log(JSON.stringify(result));
      if (result) {
        // this.commonService.startLoader();
        let userProjectId = params.data.UserProjectId;
        this.userService
          .revokeAccess(userProjectId)
          .subscribe((response: ResponseDto) => {
            if (response.success) {
              this.userService
                .getAllAccessibleUsers(this.userInfo.Id)
                .subscribe((userData: any) => {
                  this.initialiseRowData(userData.Data);
                  this.commonService.showTranslatedToasterMessage(
                    'AccessRevokeSuccessMesssage',
                    {
                      username: params.data.DisplayName,
                      name: params.data.ProjectName,
                    },
                    'success',
                  );
                  // this.commonService.stopLoader();
                });
            }
          });
      }
    });
  }

  searchFilterHandler(filterText: string) {
    this.gridApi.setGridOption('quickFilterText', filterText);
  }

  clearValue() {
    this.searchText = '';
  }

  inviteEmailHandler(event: any) {
    this.userManagementForm.patchValue({
      userDetails: event,
    });

    // Check if user is external based on email domain
    if (event && event.mail) {
      this.isExternalUser = this.commonService.isExternalUserByEmail(
        event.mail,
      );

      // If external user, automatically set access type to Viewer
      if (this.isExternalUser) {
        const viewerRole = this.accessType.find(
          (role) => role?.RoleName?.toLowerCase() === 'viewer',
        );
        if (viewerRole) {
          this.userManagementForm.patchValue({
            accessType: viewerRole.RoleId,
          });
        }
      }
    }
  }

  inviteFormHandler() {
    const { accessType, project, userDetails } = this.userManagementForm.value;
    const inviteJson = {
      inviteUsersList: [
        {
          userDetails: {
            userId: userDetails.id,
            displayName: userDetails.displayName,
            firstName: userDetails.givenName,
            lastName: userDetails.surname,
            jobTitle: userDetails.jobTitle,
            email: userDetails.mail,
            phone: '',
            isEYUser: !this.isExternalUser,
          },
          userProjectDetails: {
            projectId: project.ProjectId,
            roleId: accessType,
            projectName: project.ProjectName,
          },
        },
      ],
    };

    this.userManagementService
      .inviteUser(inviteJson)
      .subscribe((inviteResponse: ResponseDto) => {
        if (!inviteResponse.success && inviteResponse.Errors.length > 0) {
          if (
            inviteResponse.Errors[0].ErrorMessage ===
            'This user is already assigned to current project.'
          ) {
            this.commonService.showTranslatedToasterMessage(
              'UserHasAlreadyAccessMesssage',
              { username: userDetails.displayName, name: project.ProjectName },
              'warning',
            );
            this.clearForm();
          }
        } else {
          this.commonService.showTranslatedToasterMessage(
            'InviteUserMessage',
            { username: userDetails.displayName, name: project.ProjectName },
            'success',
          );
          this.clearForm();
          if (this.dialogRef) {
            this.dialogRef.close();
          }
        }
      });
  }

  clearForm() {
    this.userManagementForm.reset();
    if (this.userSearchComponent) {
      this.userSearchComponent.clearOption(null);
    }
    if (this.isFromProject) {
      const filteredProjectList = this.projectList.filter(
        (x) => x.ProjectId === this.projectId,
      );
      if (this.projectId && filteredProjectList?.length) {
        this.userManagementForm.get('project')?.setValue(this.projectList[0]);
        this.userManagementForm
          .get('projectName')
          ?.setValue(this.projectList[0].ProjectName);
      }
    }
  }

  updatePaginationSize(newPageSize: number) {
    this.currentlySelectedPageSize.value = newPageSize;
  }

  // Custom validator to check if the selected user has an email ID
  validateUserEmail(control: AbstractControl): ValidationErrors | null {
    const user = control.value;
    if (user && !user.mail) {
      return { noEmail: true }; // Return an error if no email ID is present
    }
    return null; // Valid if email ID exists
  }
}
